/**
 * ToolsModule — single-door utility hub.
 *
 * Reason for existing: utilities (Calc / Translate / FX / Market / Audio)
 * are NOT daily-life signals — they're tools the user reaches for
 * occasionally. Showing them on the home feed dilutes the signal-to-noise
 * ratio. Industry pattern (iOS, WeChat Mini Programs, Yandex Go, Naver):
 * gather utilities behind ONE door so the home stays a glance for
 * "what's happening in my life right now".
 *
 * Implementation: re-uses the existing widget objects directly (no
 * duplication). Each widget already follows the Widget contract; we
 * mount them in tabs so the user picks the tool they need with one tap.
 *
 * Spec: user request 2026-05-07 — "tools to be gathered in Tools feature
 *       instead of overfilling the home page (calculator for example)".
 */

import type { AweniContext, AweniModule } from '../../types';
import type { Widget, WidgetContext } from '../home/types';
import { getLocationService } from '../../services/LocationService';
import { WeatherService } from '../../services/WeatherService';
import {
  SmartCalc,
  TranslateBox,
  CurrencyTicker,
  MarketPulse,
  AudioRoom,
  OnThisDay,
} from '../home/widgets';

interface ToolDef {
  id: string;
  labelKey: string;
  emoji: string;
  widget: Widget;
}

const TOOLS: ToolDef[] = [
  { id: 'calc',     labelKey: 'tools_tab_calc',     emoji: '🧮', widget: SmartCalc },
  { id: 'translate', labelKey: 'tools_tab_translate', emoji: '🌐', widget: TranslateBox },
  { id: 'fx',       labelKey: 'tools_tab_fx',       emoji: '💱', widget: CurrencyTicker },
  { id: 'market',   labelKey: 'tools_tab_market',   emoji: '📈', widget: MarketPulse },
  { id: 'audio',    labelKey: 'tools_tab_audio',    emoji: '🎧', widget: AudioRoom },
  { id: 'otd',      labelKey: 'tools_tab_otd',      emoji: '📜', widget: OnThisDay },
];

const STYLE_ID = 'aweni-tools-styles';

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    .tools-shell {
      max-width: 720px; margin: 0 auto;
      padding: 16px 16px 96px;
      display: flex; flex-direction: column; gap: 14px;
    }
    .tools-header { padding: 4px 4px 0; }
    .tools-title {
      margin: 0 0 4px;
      font-size: 22px; font-weight: 800;
      color: var(--ln-text-primary, var(--text-primary, #1E293B));
    }
    .dark .tools-title { color: #F1F5F9; }
    .tools-subtitle {
      margin: 0;
      font-size: 13px; line-height: 1.5;
      color: var(--ln-text-secondary, var(--text-secondary, #6b6657));
    }
    .tools-tabs {
      display: flex; gap: 6px;
      overflow-x: auto;
      padding-bottom: 4px;
      -webkit-overflow-scrolling: touch;
    }
    .tools-tab {
      flex-shrink: 0;
      display: inline-flex; align-items: center; gap: 6px;
      padding: 8px 14px; min-height: 40px;
      border: 1px solid var(--ln-border, var(--border, rgba(0,0,0,0.10)));
      border-radius: 999px;
      background: transparent;
      color: inherit;
      font: inherit;
      font-size: 13px; font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      transition: background 120ms ease, border-color 120ms ease;
    }
    .tools-tab:hover { border-color: var(--ln-accent, #0A0A0A); }
    .tools-tab[data-active="1"] {
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border-color: var(--ln-accent, #0A0A0A);
    }
    .tools-tab-icon { font-size: 16px; line-height: 1; }
    .tools-pane { /* the active widget mounts here */ }
  `;
  document.head.appendChild(s);
}

const LS_LAST_TOOL = 'aweni_tools_last';

export default class ToolsModule implements AweniModule {
  private container: HTMLElement | null = null;
  private ctx: AweniContext | null = null;
  private weatherService: WeatherService | null = null;
  private active: string = 'calc';

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.ctx = ctx;
    this.weatherService = new WeatherService(getLocationService());
    injectStyles();

    // Restore last-used tool — if the user always lands on Translate
    // there's no reason to make them tap Calc → Translate every time.
    try {
      const last = localStorage.getItem(LS_LAST_TOOL);
      if (last && TOOLS.some((tt) => tt.id === last)) {
        this.active = last;
      }
    } catch { /* */ }

    // Honor a `?tab=...` deep link (so the home AskAweniFab or a chat
    // intent can route directly to "open Translate" if relevant).
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const tab = params.get('tab') || '';
      if (TOOLS.some((tt) => tt.id === tab)) this.active = tab;
    } catch { /* */ }

    this.render();
  }

  unmount(): void {
    if (this.container) this.container.innerHTML = '';
    this.container = null;
    this.ctx = null;
    this.weatherService = null;
  }

  /** Called by the router when the subpath changes (`/tools/translate` etc.) */
  onRouteChange(sub?: string): void {
    if (!sub) return;
    const id = sub.split('/')[0];
    if (TOOLS.some((tt) => tt.id === id)) {
      this.active = id;
      this.render();
    }
  }

  // ---------------------------------------------------------------------

  private render(): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';

    this.container.innerHTML = `
      <div class="tools-shell" dir="${isRtl ? 'rtl' : 'ltr'}">
        <header class="tools-header">
          <h1 class="tools-title">🛠 ${escHtml(t('tools_title') || 'Tools')}</h1>
          <p class="tools-subtitle">${escHtml(t('tools_subtitle') || 'Calculator, translator, currencies, markets, audio — one tap each.')}</p>
        </header>
        <nav class="tools-tabs" role="tablist" aria-label="${escAttr(t('tools_tabs_aria') || 'Available tools')}">
          ${TOOLS.map((tool) => `
            <button type="button" class="tools-tab" role="tab"
              data-tool="${tool.id}" data-active="${tool.id === this.active ? '1' : '0'}"
              aria-selected="${tool.id === this.active ? 'true' : 'false'}">
              <span class="tools-tab-icon" aria-hidden="true">${tool.emoji}</span>
              <span>${escHtml(t(tool.labelKey) || tool.id)}</span>
            </button>
          `).join('')}
        </nav>
        <div class="tools-pane" data-tools-pane></div>
      </div>
    `;

    this.container.querySelectorAll<HTMLButtonElement>('[data-tool]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.tool || '';
        if (id && id !== this.active) {
          this.active = id;
          try { localStorage.setItem(LS_LAST_TOOL, id); } catch { /* */ }
          this.render();
        }
      });
    });

    this.renderPane();
  }

  private renderPane(): void {
    if (!this.container || !this.ctx) return;
    const pane = this.container.querySelector<HTMLElement>('[data-tools-pane]');
    if (!pane) return;
    const tool = TOOLS.find((tt) => tt.id === this.active);
    if (!tool) {
      pane.innerHTML = '';
      return;
    }
    pane.innerHTML = '';
    const wctx = this.buildWidgetContext();
    try {
      tool.widget.render(pane, 'expanded', wctx);
    } catch (err) {
      pane.innerHTML = `<div style="padding:14px;font-size:12px;color:var(--ln-text-secondary,#6b6657);">${escHtml(this.ctx.i18n.t('common_error') || 'Something went wrong.')}</div>`;
      void err;
    }
  }

  private buildWidgetContext(): WidgetContext {
    const ctx = this.ctx as AweniContext;
    return {
      t: ctx.i18n.t.bind(ctx.i18n),
      locale: ctx.i18n.locale || 'en',
      isRtl: ctx.i18n.dir === 'rtl',
      isDark: ctx.theme.isDark,
      userName: ctx.auth.user?.name?.split(' ')[0] || '',
      isGuest: !!ctx.auth.user?.is_guest,
      navigate: (m, p) => { void p; ctx.navigate(m); },
      showNotification: (msg, type) => ctx.showNotification(msg, type),
      getLocationService: () => getLocationService().getState(),
      refreshLocation: async () => { await getLocationService().refresh(); },
      getWeatherData: () => this.weatherService?.getData() ?? null,
    };
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
